import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { toast } from 'react-toastify';
import ProgramTypeFilter from '../../Program/components/learning-lifecycle/ProgramTypeFilter';
import type { LearningPillar, ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';
import type { MemberProgramCardData, MemberRoleKey } from './types';
import MemberPillarColumn from './MemberPillarColumn';
import MemberProgramCard from './MemberProgramCard';
import { useMemberLearningFramework } from './useMemberLearningFramework';
import { byDisplayOrder, pickResumeProgram } from './resumeTarget';
import UserProgramsDashboard from '../UserProgramsDashboard';
import LearnerGamificationBar from '../../../../../components/gamification/LearnerGamificationBar';
import MemberInsightsPanel from './MemberInsightsPanel';
import { AssessmentPanel, assessmentCards, isAssessmentId } from '../../capability-setup/learner/AssessmentCards';
import { useMyAssessments } from '../../../../../queries/useCapability';
import { TrainingPanel } from '../../capability-setup/learner/ProgramPanel';
import DevelopmentTierPanelLegacy from '../../../../../components/gamification/DevelopmentTierPanelLegacy';
import { useCapabilityReleaseForMembers } from '../../../../../hooks/useCapabilityRelease';
import { useLearnerGamification } from '../../../../../components/gamification/useLearnerGamification';
import { splitByUrgency } from '../../../../../components/gamification/dueUrgency';
import useTierActions from './useTierActions';
import ElliottOrb from '../../../../../components/gamification/ElliottOrb';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { getUserDetails } from '../../../../../utils/orgName';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { updateSelectedId } from '../../../../../redux/slices/selectedGroupSlice';
import { setSelectedValue } from '../../../../../redux/slices/selectedValueSlice';
import { groupRoleListUpdate, refreshUserGroupList } from '../../../../../redux/slices/groupRolesListSlice';
import { entryForProgram, missingRoleListEntries, withEntries } from './roleListSync';
import MemberPathwaySkeleton from './MemberPathwaySkeleton';
import { API } from '../../../../../api';
import { fadeInSx } from '../../../../../components/ui/DashboardSkeletons';
import type { InsightsView } from '../../Program/components/learning-lifecycle/AdminInsightsPanel';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';
import PhoneSegments from '../../../../../mobile/PhoneSegments';

// Synthetic "Unassigned" column for programs the admin hasn't categorised
// yet. Rendered first when present so the user sees their programs
// even before any pillar assignment. Read-only on the member side —
// reassignment happens on the admin dashboard.
// Droppable id (and saved-order key) for the synthetic Unassigned column.
const UNASSIGNED_DROPPABLE_ID = '__unassigned__';

const UNASSIGNED_PILLAR: LearningPillar = {
  id: '',
  name: 'Unassigned',
  order: -1,
  icon: null
};

/**
 * Member Learning Lifecycle Dashboard — pillar-based read-only mirror of the
 * Admin Learning Framework Dashboard. Replaces the legacy Program Portfolio
 * Kanban (Not Started / In Progress / Completed). GA: shown to every org, no
 * flag. Chrome reads "MY LEARNING PATHWAY" (rendered by Layout.tsx).
 *
 * Spec: PRD — Member Learning Lifecycle Dashboard
 * https://dosen.atlassian.net/wiki/spaces/DA/pages/336199683
 *
 * Build progress — see Tech Plan §4:
 *   ✓ Step 1  Page shell + mount
 *   ✓ Step 2  Top bar (legend + search, no Edit Framework — read-only)
 *   ✓ Step 3  Pillar columns scaffold (read-only headers, empty bodies)
 *   ✓ Step 4  Program card — Layout 1 (Masterclass)
 *   ✓ Step 5  Program cards — Layouts 2 & 3 (Mentorship Mentor/Learner)
 *   ✓ Step 6  Program cards — Layouts 4 & 5 (Guided MC Mentor/Learner)
 *   ✓ Step 7  Mentor/Learner toggle on dual-role cards
 *   ✓ Step 8  Greyed-out completed treatment (opacity 0.45)
 *   ✓ Step 9  Dummy data — 3 programs per pillar, all scenarios covered
 *   ✓ Step 10 Empty state ("Your Learning Lifecycle will be available soon.")
 *   ✓ Step 11 Search behaviour (real-time name filter)
 *     Step 12 PR opens (revert dev flag overrides before merge)
 */

// Nav arrow — same Figma spec as Admin: bg #565656, padding 12/10, radius 6,
// 1px #444444 border, backdrop-blur 1.1px; chevron 1.5px white stroke.
const NavArrowButton: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
  'aria-label': string;
}> = ({ direction, onClick, 'aria-label': ariaLabel }) => {
  const Icon = direction === 'right' ? ChevronRightIcon : ChevronLeftIcon;
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        position: 'absolute',
        top: 30,
        [direction]: 0,
        pl: '12px',
        pr: '12px',
        pt: '10px',
        pb: '10px',
        bgcolor: '#565656',
        border: '1px solid #444444',
        borderRadius: '6px',
        backdropFilter: 'blur(1.1px)',
        WebkitBackdropFilter: 'blur(1.1px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: '#666666' }
      }}
    >
      <Icon sx={{ color: '#FDFDFD', fontSize: 16, strokeWidth: 1.5 }} />
    </Box>
  );
};

// Phone: the pathway and the Capability / Activity panel sit side by side on
// desktop; a phone has room for one, so a segmented control picks it.
type PhoneView = 'programs' | InsightsView;


/**
 * The lesson saves answers only for the program the server last saw the
 * learner open (users.lastseen): saveAnswers answers 401 for any other. Every
 * other way into a program (the program drawer, the role switch, the bell)
 * tells the server; going into a lesson from here must too. Waits for the
 * answer, a few seconds at most, so the lesson's first answer never beats it.
 */
const rememberProgram = (entry: { id: string; name: string; role: string }) =>
  Promise.race([
    Promise.resolve(API.updateLastseen({ id: entry.id, role: entry.role, name: entry.name })).catch(() => undefined),
    new Promise<void>((resolve) => {
      setTimeout(resolve, 4000);
    })
  ]);

// The Continue pill shows a name in full up to 50 characters, and cuts a longer one there.
const CONTINUE_LABEL_MAX = 50;
const capLabel = (label: string) =>
  label.length > CONTINUE_LABEL_MAX ? `${label.slice(0, CONTINUE_LABEL_MAX - 1).trimEnd()}…` : label;

const MemberLearningLifecycleDashboard: React.FC = () => {
  // The learner's Capability / Activity panel, or the Learning Tier panel before the release (utils/capabilityRelease.ts).
  // Before the release is on for an org, its cards open the program view they always did.
  // What members get: in admin mode it stays off here, for an admin who switched to their pathway too.
  const capabilityRelease = useCapabilityReleaseForMembers();
  const InsightsPanel = capabilityRelease ? MemberInsightsPanel : DevelopmentTierPanelLegacy;
  const isMobileApp = useIsMobileApp();
  const [phoneView, setPhoneView] = useState<PhoneView>('programs');
  const [openStages, setOpenStages] = useState<Set<string>>(() => new Set());
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const dispatch = useAppDispatch();
  // The role list — what every way into a program looks the program up in, and
  // what the program cards write to lastseen before deep-linking into a lesson.
  // Read once at sign-in and cached for five minutes, so the effect below keeps
  // it in step with the board (roleListSync.ts).
  const roleList = useAppSelector((state) => state.groupRoleList);
  const groupRoleEntries = useMemo(() => roleList.data || [], [roleList.data]);
  const [searchTerm, setSearchTerm] = useState('');
  // Program-type filter — same as Admin. Empty = every type shown (the
  // default); a chip toggles its type in and out, alongside search.
  const [selectedTypes, setSelectedTypes] = useState<ProgramTypeKey[]>([]);
  const toggleType = (key: ProgramTypeKey) =>
    setSelectedTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  // When the user clicks Continue on a card we mount UserProgramsDashboard
  // hidden as a sibling — its MUI Drawer (portal'd to body) is what the
  // admin actually sees on top of the LL view. Picking a different card
  // updates this state and re-opens the drawer for that program.
  const [drawerProgramId, setDrawerProgramId] = useState<string | null>(null);
  const [drawerPreferredRole, setDrawerPreferredRole] = useState<string | null>(null);
  // Stub built from the LL program — lets UserProgramsDashboard open
  // the drawer immediately without waiting for its own API fetch.
  const [drawerProgramStub, setDrawerProgramStub] = useState<{
    id: string;
    name: string;
    roles: ('mentor' | 'mentee')[];
    model?: string;
    status?: 'not-started' | 'in-progress' | 'completed';
    // `raw` carries the progress the LL card already knows so the drawer's
    // progress bar shows the same value instantly instead of spinning while
    // it waits for UserProgramsDashboard's own (separate) fetch to hydrate.
    raw?: {
      goalProgress?: number;
      timeRemainingMinutes?: number | null;
      /** Deep link for the drawer's "Next Lesson" — same target as the card's Continue. */
      resumeLesson?: { moduleId: string; taskId: string; lessonName: string } | null;
      locked?: boolean;
    };
  } | null>(null);
  // Live pillars + programs from GET /members/learning-framework/{orgId}.
  const { pillars, programs, loading, error } = useMemberLearningFramework();
  // The Assessments this learner is sent (GET /v3/member/assessments): each a card on the board, opening its own panel.
  const { data: runAssessmentsData } = useMyAssessments(capabilityRelease);
  const runAssessments = useMemo(() => runAssessmentsData ?? [], [runAssessmentsData]);
  const [openAssessmentId, setOpenAssessmentId] = useState<string | null>(null);
  // A Training's card opens its panel here too. Mentorship keeps the full program view.
  const [openTraining, setOpenTraining] = useState<MemberProgramCardData | null>(null);
  const assessmentPrograms = useMemo(
    () =>
      assessmentCards(
        runAssessments,
        pillars.map((p) => p.id)
      ),
    [runAssessments, pillars]
  );

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // A program this learner joined since the role list was read is on the board
  // and not in the list: auto-enrolment when onboarding saved their member
  // attributes (a learner invited to the org with no program), an invite
  // finished while they were signed in. The board is the fresher reading of
  // the same membership, so the list takes the board's programs at once, from
  // the board's own data, and is re-read from the server once per such set so
  // the cache agrees too. Until then every way into one of those programs
  // opened the lesson page with no program, and the Header, which sends the
  // session back to the start when the selected program is not in the list,
  // did so to an admin who is also a learner. Waits for the first read of the
  // list: a program the server already knows arrives with it.
  const refreshedForRef = useRef('');
  useEffect(() => {
    if (loading || error || roleList.loading || roleList.data === undefined) return;
    const missing = missingRoleListEntries(roleList.data, programs);
    if (missing.length === 0) return;
    dispatch(groupRoleListUpdate.update(withEntries(roleList.data, missing)));
    const key = missing.map((e) => `${e.id}_${e.role}`).join(',');
    if (refreshedForRef.current === key) return;
    refreshedForRef.current = key;
    dispatch(refreshUserGroupList());
  }, [loading, error, roleList.loading, roleList.data, programs, dispatch]);

  // Horizontal scroll affordance — show chevrons when pillar columns overflow
  // and there's still room to scroll in that direction.
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = columnsRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
    // isMobileApp: the columns row only exists on the desktop layout, so re-attach after a resize swaps it in.
  }, [pillars.length, isMobileApp]);

  const handleScrollLeft = () => {
    const el = columnsRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.6, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const el = columnsRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.6, behavior: 'smooth' });
  };

  // Open the legacy program drawer for a program — same flow as a card's
  // Continue (drawer's own "Continue Program" then deep-links into the
  // mentee/mentor experience).
  const openProgramDrawer = (program: MemberProgramCardData, role: MemberRoleKey) => {
    setDrawerPreferredRole(role);
    setDrawerProgramStub({
      id: program.id,
      name: program.name,
      roles: program.roles,
      model: program.model || undefined,
      status: program.status,
      raw: {
        goalProgress: program.goalProgress,
        timeRemainingMinutes: program.timeRemainingMinutes,
        // The drawer's "Next Lesson" needs the same deep link the card's
        // Continue uses; without it the drawer could only route to the
        // program landing, which bounces while the profile is still loading.
        resumeLesson: program.resumeLesson || null,
        locked: program.locked === true
      }
    });
    setDrawerProgramId(null);
    setTimeout(() => setDrawerProgramId(program.id), 0);
  };

  // A course link (/app/home/course/:grpId — Elliott's course recommendations,
  // in the chat and in Teams) lands here with { openProgramId } in the route
  // state: open that course's drawer once the learner's programs have loaded.
  const requestedProgramId = (routeLocation.state as { openProgramId?: string } | null)?.openProgramId;
  useEffect(() => {
    if (!requestedProgramId || loading) return;
    navigate(routeLocation.pathname, { replace: true, state: null });
    if (error) return; // the fetch failed — already toasted; can't tell if they're enrolled
    const program = programs.find((p) => p.id === requestedProgramId);
    if (!program) {
      toast.info("You're not enrolled in that program yet. Ask the program owner to enroll you.");
      return;
    }
    if (program.locked !== true) openProgramDrawer(program, program.roles[0] ?? 'mentee');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedProgramId, loading, programs, error]);

  // Jump straight into the resume lesson (no drawer): set the active program
  // in redux + lastseen — the lesson page reads grpId from selectedGroup — then
  // navigate to the lesson route. Falls back to the program drawer when we
  // don't have a resume lesson (e.g. every lesson already complete).
  const continueToLesson = async (program: MemberProgramCardData) => {
    // Defense: a locked program answers 423 on every lesson read, so never
    // navigate into one even if a caller hands us the program anyway.
    if (program.locked === true) return;
    const rl = program.resumeLesson;
    if (!rl) {
      openProgramDrawer(program, program.roles[0] ?? 'mentee');
      return;
    }
    // A learner who has just been enrolled may not be in the role list yet
    // (roleListSync.ts). Leaving the selection unset while navigating anyway
    // opened the lesson page with no program; the card already knows it, and
    // the list takes the entry too, so every lookup in the app agrees.
    const { entry, known } = entryForProgram(groupRoleEntries, program, 'mentee');
    if (!known) dispatch(groupRoleListUpdate.update(withEntries(groupRoleEntries, [entry])));
    dispatch(updateSelectedId(entry));
    dispatch(setSelectedValue(`${entry.id}_${entry.role}`));
    try {
      localStorage.setItem('lastseen', JSON.stringify(entry));
    } catch {
      /* localStorage full — non-fatal */
    }
    await rememberProgram(entry);
    navigate(`/app/home/mentee/level/${rl.moduleId}/task/${rl.taskId}`);
  };

  // Set the active program in redux + lastseen so the lesson page (which reads
  // grpId from selectedGroup) opens against the right program. Shared by the
  // Continue pill and by every action row in the Development Tier panel.
  const selectProgram = useCallback(
    (programId: string) => {
      // Same reason as continueToLesson: a learner enrolled moments ago may not
      // be in the role list yet. Returning here left Redux unset while the
      // caller navigated into the lesson anyway, which is how a learner ended
      // up on a lesson page that did not know which program it belonged to.
      const name = programs.find((p) => p.id === programId)?.name || '';
      const { entry, known } = entryForProgram(groupRoleEntries, { id: programId, name }, 'mentee');
      if (!known) dispatch(groupRoleListUpdate.update(withEntries(groupRoleEntries, [entry])));
      dispatch(updateSelectedId(entry));
      dispatch(setSelectedValue(`${entry.id}_${entry.role}`));
      try {
        localStorage.setItem('lastseen', JSON.stringify(entry));
      } catch {
        /* localStorage full — non-fatal */
      }
      return rememberProgram(entry);
    },
    [groupRoleEntries, programs, dispatch]
  );

  // Development Tier panel — the learner's tier and, behind each measure's How
  // to Improve dropdown, the lessons that are actually holding that measure
  // back. Lifted here (rather than fetched inside the panel) so the bar and the
  // panel share one /gamification/learner/summary call.
  const { summary: gamificationSummary, loading: gamificationLoading } = useLearnerGamification();

  // Open one lesson in Improve Performance mode — the same route the card CTA
  // uses (L-11/L-14), aimed at the specific item the learner clicked rather
  // than the first one in the program's queue.
  const openImprove = useCallback(
    async (programId: string, levelId: string, taskId: string) => {
      await selectProgram(programId);
      navigate(`/app/home/mentee/level/${levelId}/task/${taskId}?improve=1`);
    },
    [selectProgram, navigate]
  );
  const askElliott = useCallback(() => navigate('/app/charlie'), [navigate]);

  // Real-time name filter — same behaviour as Admin dashboard search.
  // Applied before distributing programs into pillar columns so empty
  // columns collapse naturally when no match exists in that pillar.
  // Sorted by urgency: soonest due date first, programs with no due date last
  // (name as tiebreak so the order is stable between renders/refetches).
  const filteredPrograms = [...assessmentPrograms, ...programs]
    .filter((p) => searchTerm === '' || p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((p) => selectedTypes.length === 0 || selectedTypes.includes(p.programType))
    .slice()
    .sort((a, b) => {
      if (a.dueBy == null && b.dueBy == null) return a.name.localeCompare(b.name);
      if (a.dueBy == null) return 1;
      if (b.dueBy == null) return -1;
      if (a.dueBy !== b.dueBy) return a.dueBy - b.dueBy;
      return a.name.localeCompare(b.name);
    });

  const hasPillars = pillars.length > 0;

  // Programs in the Urgent Zone of the gamification hub's Programs Due flyout.
  // The cards flag exactly these with the red "!" and outline, so the two
  // surfaces never disagree about which programs are urgent.
  const urgentProgramNames = new Set(
    splitByUrgency(gamificationSummary?.programsDue || []).urgent.map((p) => p.programName)
  );

  // ---- Reorder cards within a pillar (learner) ----------------------------
  // Drag to reorder inside a column only; the order is per learner, saved in
  // this browser. Programs with no saved position keep the due-date order,
  // after the ones the learner has placed.
  const orderStorageKey = `memberPillarOrder:${getUserDetails().userId || 'anon'}`;
  const [pillarOrder, setPillarOrder] = useState<Record<string, string[]>>(() => {
    try {
      return JSON.parse(localStorage.getItem(orderStorageKey) || '{}') || {};
    } catch {
      return {};
    }
  });
  const orderedFor = (pillarKey: string, list: MemberProgramCardData[]) => {
    const saved = pillarOrder[pillarKey];
    // No personal order yet: fall back to the order the admin arranged on the
    // Learning Dashboard (groups.displayOrder), not the API's natural order.
    if (!saved || saved.length === 0) return byDisplayOrder(list);
    const rank = new Map(saved.map((id, i) => [id, i]));
    return list
      .slice()
      .sort((a, b) => (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  };
  const pillarList = (pillarKey: string) =>
    orderedFor(
      pillarKey,
      filteredPrograms.filter((p) =>
        pillarKey === UNASSIGNED_DROPPABLE_ID ? !p.learningPillarId : p.learningPillarId === pillarKey
      )
    );
  // Resume target for the gamification bar's Continue pill, in board order:
  // the unassigned column renders first, then each pillar in its order, each
  // column already sorted. The pill therefore points at the first card the
  // learner could open, so ordering the columns orders the CTA. Locked and
  // completed programs are skipped; null means the bar shows no pill at all.
  const boardOrder = [...pillarList(UNASSIGNED_DROPPABLE_ID), ...pillars.flatMap((pillar) => pillarList(pillar.id))];
  const resumeProgram = pickResumeProgram(boardOrder);
  // The pill names the lesson when the program can say which one comes next;
  // `pickResumeProgram` has already stepped over the programs that cannot.
  // When none can — a board of mentorship programs, say — it still points at
  // the program, whose drawer is a real destination, and says only its name
  // rather than promising a lesson it cannot open. No pill at all is reserved
  // for a learner with genuinely nothing to resume.
  // The pill says the lesson's name, in full up to 50 characters; the program's is on hover.
  // A phone has no hover, so its pill (a full-width row that wraps) says the whole name.
  const resumeLabel = resumeProgram ? (resumeProgram.resumeLesson ? resumeProgram.resumeLesson.lessonName : resumeProgram.name) : '';
  const resumeTarget = resumeProgram
    ? {
        label: isMobileApp ? resumeLabel : capLabel(resumeLabel),
        tooltip: resumeProgram.resumeLesson
          ? `${resumeProgram.name}: ${resumeProgram.resumeLesson.lessonName}`
          : resumeProgram.name,
        progressPct: resumeProgram.goalProgress ?? null,
        timeRemainingMinutes: resumeProgram.timeRemainingMinutes ?? null,
        onContinue: () => continueToLesson(resumeProgram)
      }
    : null;

  const handleCardDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // Only within the same pillar — a drop into another column is ignored.
    if (!destination || destination.droppableId !== source.droppableId || destination.index === source.index) return;
    const key = source.droppableId;
    const ids = pillarList(key).map((p) => p.id);
    const [moved] = ids.splice(source.index, 1);
    ids.splice(destination.index, 0, moved);
    // Keep saved positions for cards hidden by search / type filter.
    const hidden = (pillarOrder[key] || []).filter((id) => !ids.includes(id));
    const next = { ...pillarOrder, [key]: [...ids, ...hidden] };
    setPillarOrder(next);
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(next));
    } catch {
      /* storage blocked — the order just won't persist */
    }
  };
  // What a card's Open opens: an Assessment's panel, a Training's panel, or the program drawer.
  const openCard = (program: MemberProgramCardData, role: MemberRoleKey) => {
    if (isAssessmentId(program.id)) setOpenAssessmentId(program.id);
    else if (program.model === 'mentorship' || !capabilityRelease) openProgramDrawer(program, role);
    else setOpenTraining(program);
  };
  const renderMemberCard = (program: MemberProgramCardData, index: number) => (
    <Draggable key={program.id} draggableId={program.id} index={index}>
      {(dragProvided, snapshot) => (
        <Box
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          {...dragProvided.dragHandleProps}
          sx={{
            flex: '0 0 auto',
            borderRadius: '8px',
            boxShadow: snapshot.isDragging ? '0 12px 32px rgba(0, 0, 0, 0.4)' : 'none',
            transition: 'box-shadow 0.15s'
          }}
        >
          <MemberProgramCard
            program={program}
            urgent={urgentProgramNames.has(program.name)}
            onContinue={(role) => openCard(program, role)}
          />
        </Box>
      )}
    </Draggable>
  );

  // Outstanding work per measure. Uses the unfiltered program list on purpose:
  // the panel is about the learner's whole tier, not the current search.
  const { actionsByMeasure, loading: tierActionsLoading } = useTierActions({
    programs,
    onOpenImprove: openImprove,
    onContinueProgram: continueToLesson,
    onAskElliott: askElliott
  });

  // The panels a card opens. Both layouts host the same ones.
  const programPanels = (
    <>
      <AssessmentPanel
        assessment={runAssessments.find((run) => run.id === openAssessmentId) ?? null}
        onClose={() => setOpenAssessmentId(null)}
        onContinue={(id) => navigate(`/app/assessment/${id}`)}
      />
      <TrainingPanel
        program={openTraining}
        onClose={() => setOpenTraining(null)}
        onContinue={(program) => {
          setOpenTraining(null);
          continueToLesson(program);
        }}
        onDetails={(program) => {
          setOpenTraining(null);
          openProgramDrawer(program, program.roles[0] ?? 'mentee');
        }}
      />
      {drawerProgramId && (
        <UserProgramsDashboard
          hidden
          initialOpenProgramId={drawerProgramId}
          initialOpenPreferredRole={drawerPreferredRole ?? undefined}
          initialProgramStub={drawerProgramStub ?? undefined}
        />
      )}
    </>
  );

  // Phone: a search opens the stages it found something in; clearing it closes them again.
  const searchedStages = searchTerm.trim()
    ? [UNASSIGNED_DROPPABLE_ID, ...pillars.map((pillar) => pillar.id)]
        .filter((key) => pillarList(key).length > 0)
        .join('|')
    : '';
  useEffect(() => {
    if (isMobileApp) setOpenStages(new Set(searchedStages ? searchedStages.split('|') : []));
  }, [isMobileApp, searchedStages]);

  // Phone: one column. The summary card (Continue, streak, rank) on top, then a
  // segmented control between the pathway and the learner's Capability /
  // Activity, which share the desktop screen side by side.
  if (isMobileApp) {
    const filtering = searchTerm !== '' || selectedTypes.length > 0;
    // Every column in board order, the unassigned one first; a pillar with no
    // programs is left out: a phone has no row of columns to keep in shape.
    const sections = [
      { key: UNASSIGNED_DROPPABLE_ID, pillar: UNASSIGNED_PILLAR, list: pillarList(UNASSIGNED_DROPPABLE_ID) },
      ...pillars.map((pillar) => ({ key: pillar.id, pillar, list: pillarList(pillar.id) }))
    ].filter((section) => section.list.length > 0);
    const segments: { key: PhoneView; label: string }[] = capabilityRelease
      ? [
          { key: 'programs', label: 'Programs' },
          { key: 'gaps', label: 'Capability' },
          { key: 'tier', label: 'Activity' }
        ]
      : [
          { key: 'programs', label: 'Programs' },
          { key: 'tier', label: 'Learning Tier' }
        ];
    // A view the segments no longer offer (the release switched off with an org change) falls back to Programs.
    const shownView: PhoneView = segments.some((segment) => segment.key === phoneView) ? phoneView : 'programs';
    const insightsProps = {
      medals: gamificationSummary?.medals ?? null,
      loading: gamificationLoading,
      actionsByMeasure,
      actionsLoading: tierActionsLoading
    };
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', color: '#FDFDFD' }}>
        <ElliottOrb />
        <LearnerGamificationBar resumeTarget={resumeTarget} summary={gamificationSummary} loading={loading} />
        <PhoneSegments value={shownView} options={segments} onChange={setPhoneView} />

        {shownView !== 'programs' ? (
          capabilityRelease ? (
            <MemberInsightsPanel {...insightsProps} view={shownView} />
          ) : (
            <DevelopmentTierPanelLegacy
              {...insightsProps}
              sx={{ [MOBILE_SX]: { flex: 'none', minWidth: 0, height: 'auto', mr: 0 } }}
            />
          )
        ) : loading ? (
          <MemberPathwaySkeleton />
        ) : hasPillars ? (
          // The same frame as the Capability and Activity tabs.
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              p: '12px',
              bgcolor: '#1C1C1C',
              border: '1px solid rgba(247, 148, 32, 0.45)',
              borderRadius: '8px',
              boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
              ...fadeInSx
            }}
          >
            <TextField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
              size="small"
              inputProps={{ 'aria-label': 'Search programs', enterKeyHint: 'search' }}
              // The admin's search bar.
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0, 0, 0, 0.70)',
                  borderRadius: '6px',
                  color: '#FDFDFD',
                  fontFamily: 'Inter, sans-serif',
                  // 16px keeps iOS Safari from zooming in on focus.
                  fontSize: 16,
                  px: '12px',
                  '& fieldset': { borderColor: '#444444' },
                  '&:hover fieldset': { borderColor: '#565656' },
                  '&.Mui-focused fieldset': { borderColor: '#F79420' }
                },
                '& .MuiOutlinedInput-input': { py: '10px', px: 0 },
                '& .MuiOutlinedInput-input::placeholder': { color: '#868686', opacity: 1 }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
            {/* Type chips: one row that scrolls sideways rather than wrapping. */}
            <Box
              sx={{
                mx: '-12px',
                px: '12px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '& > div': { width: 'max-content' }
              }}
            >
              <ProgramTypeFilter selected={selectedTypes} onToggle={toggleType} />
            </Box>

            {/* The stages one under another, each a tab that opens its cards, closed to start. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <DragDropContext onDragEnd={handleCardDragEnd}>
                {sections.map((section) => {
                  const open = openStages.has(section.key);
                  return (
                    <MemberPillarColumn
                      key={section.key}
                      pillar={section.pillar}
                      programCount={section.list.length}
                      droppableId={section.key}
                      collapsed={!open}
                      onToggleCollapsed={() =>
                        setOpenStages((prev) => {
                          const next = new Set(prev);
                          if (open) next.delete(section.key);
                          else next.add(section.key);
                          return next;
                        })
                      }
                    >
                      {section.list.map((program, index) => renderMemberCard(program, index))}
                    </MemberPillarColumn>
                  );
                })}
              </DragDropContext>
            </Box>
            {sections.length === 0 && (
              <Typography
                sx={{
                  py: '32px',
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  color: '#868686'
                }}
              >
                {filtering
                  ? 'No programs match your search.'
                  : 'Your programs will appear here as soon as you are enrolled in one.'}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography
            sx={{ py: '48px', textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#868686' }}
          >
            Your programs will appear here as soon as you are enrolled in one.
          </Typography>
        )}
        {programPanels}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0F0F0F',
        color: '#FDFDFD'
        // No px / pt — the parent ProgramDetailsWrapper in Layout.tsx already
        // adds 25px padding on all sides. Stacking pt on top of that produced
        // the asymmetric ~49px gap below the chrome divider.
      }}
    >
      {/* Elliott corner orb (doc §1.8) — mounted here; a later pass moves it
          to the learner Layout so it lives on every page. */}
      <ElliottOrb />

      {/* Header row — one line, left to right: Continue CTA + course gauge +
          gamification chips, then the legend and search as a right-hand group.
          No Edit Framework CTA: Member view is strictly read-only per PRD
          §"What Members Cannot Do". Never wraps (Cian, 2026-10-16): the
          right-hand group gives first, down to the legend's width, then the
          Continue pill's lesson name truncates. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 2,
          flexWrap: 'nowrap',
          mb: 2,
          minHeight: 40
        }}
      >
        {/* Gamification hub — low-intensity cluster: Continue + course gauge +
            stat chips with on-demand flyouts, hard left. Renders only from
            this learner's own data and disappears entirely when there is none.
            See docs/EPIC-gamification.md. */}
        {/* At least as wide as the Capability / Activity panel below (30%), so the right edges line up,
            and wider when the Continue pill's lesson name needs the room. */}
        <Box sx={{ flex: '0 1 auto', minWidth: '30%', display: 'flex', '& > *': { flex: 1 } }}>
          <LearnerGamificationBar resumeTarget={resumeTarget} summary={gamificationSummary} loading={loading} />
        </Box>

        {/* Right-hand group — legend stacked above the search and centred over
            it. Grouped (rather than two separately positioned items) so the
            pair stays aligned to each other and to the right edge. */}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: 1,
            // Shrinks well before the gamification bar does, and never past the legend above the search.
            flex: '0 1000 392px',
            minWidth: 'min-content'
          }}
        >
          {/* Program-type filter — same chips as Admin, centred over the search.
              Shows all types by default; a chip narrows the columns to it. */}
          <ProgramTypeFilter selected={selectedTypes} onToggle={toggleType} />

          {/* Search (far right, under the legend) — per Figma, Member places
              this in the right slot (Admin had it page-centred to leave room
              for Edit Framework). Spec: padding 10/12, bg rgba(0,0,0,0.70),
              radius 6, border 1px #444444; input Inter 14/19.88 weight 400
              #FDFDFD; icon 18px #FDFDFD on the right. Figma width is ~392px;
              the column caps it there and lets it shrink on narrow viewports. */}
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0, 0, 0, 0.70)',
                borderRadius: '6px',
                color: '#FDFDFD',
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '19.88px',
                px: '12px',
                '& fieldset': { borderColor: '#444444' },
                '&:hover fieldset': { borderColor: '#565656' },
                '&.Mui-focused fieldset': { borderColor: '#F79420' }
              },
              '& .MuiOutlinedInput-input': { py: '10px', px: 0 },
              '& .MuiOutlinedInput-input::placeholder': { color: '#868686', opacity: 1 }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 1 }}>
                  <SearchIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      {/* Body: placeholders shaped like the page while the framework fetch is
          in flight, then the pillar columns row (faded in) or empty-state message. */}
      {loading ? (
        <MemberPathwaySkeleton />
      ) : hasPillars ? (
        // Body — Development Tier on the left, the pillar columns on the right.
        // Mirrors the admin dashboard's Gaps & Risks / Learning Framework split:
        // the panel says what to work on, the framework is the work.
        <Box sx={{ flex: 1, minHeight: 0, pb: 3, display: 'flex', flexDirection: 'row', gap: 0, ...fadeInSx }}>
          <InsightsPanel
            medals={gamificationSummary?.medals ?? null}
            loading={gamificationLoading}
            actionsByMeasure={actionsByMeasure}
            actionsLoading={tierActionsLoading}
          />

          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              height: '100%',
              // The same surface as the Capability / Activity panel beside it.
              bgcolor: '#1C1C1C',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <Box
              ref={columnsRef}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'row',
                // No flex gap — each column carries its own 20px padding (Figma).
                gap: 0,
                // Fewer than 4 columns sit centred. `safe` falls back to the start edge
                // once they overflow, so a 5th column still scrolls from the left.
                justifyContent: 'safe center',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'thin'
              }}
            >
              <DragDropContext onDragEnd={handleCardDragEnd}>
                {(() => {
                  const unassigned = pillarList(UNASSIGNED_DROPPABLE_ID);
                  if (unassigned.length === 0) return null;
                  return (
                    <MemberPillarColumn
                      key={UNASSIGNED_DROPPABLE_ID}
                      pillar={UNASSIGNED_PILLAR}
                      programCount={unassigned.length}
                      droppableId={UNASSIGNED_DROPPABLE_ID}
                    >
                      {unassigned.map((program, index) => renderMemberCard(program, index))}
                    </MemberPillarColumn>
                  );
                })()}
                {pillars.map((pillar) => {
                  const pillarPrograms = pillarList(pillar.id);
                  return (
                    <MemberPillarColumn
                      key={pillar.id}
                      pillar={pillar}
                      programCount={pillarPrograms.length}
                      droppableId={pillar.id}
                    >
                      {pillarPrograms.map((program, index) => renderMemberCard(program, index))}
                    </MemberPillarColumn>
                  );
                })}
              </DragDropContext>
            </Box>

            {canScrollLeft && (
              <NavArrowButton direction="left" onClick={handleScrollLeft} aria-label="Scroll columns left" />
            )}
            {canScrollRight && (
              <NavArrowButton direction="right" onClick={handleScrollRight} aria-label="Scroll columns right" />
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              color: '#868686'
            }}
          >
            Your programs will appear here as soon as you are enrolled in one.
          </Typography>
        </Box>
      )}

      {/* Hidden host for the legacy expanded program drawer. When a card
          Continue is clicked, drawerProgramId is set and this mounts
          UserProgramsDashboard with `hidden` — the only visible piece is
          its MUI Drawer (portaled to document.body). The drawer's own
          "Continue Program" button handles navigation to /home/mentee
          or /home/mentor. Only mounted once a card is clicked so we
          don't pay the cost on every dashboard view. */}
      {programPanels}
    </Box>
  );
};

export default MemberLearningLifecycleDashboard;
